import { useQueryClient } from '@tanstack/react-query';
import {
  MutableRefObject,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { PAYMENT_SINGLE_MONTH_QUERY_KEY } from 'src/contexts/MarketplacePaymentDataContext/MarketplacePaymentDataContext.constants';
import { DeleteMarketplacePaymentDialog } from 'src/dialogs/DeleteMarketplacePaymentDialog/DeleteMarketplacePaymentDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  MarketplacePaymentClient,
  MarketplaceSalePayment,
} from 'src/WebApiController';

export interface DeleteMarketplacePaymentRefActions {
  showDeleteMarketplacePaymentDialog: () => void;
}

interface DeleteMarketplacePaymentProps {
  marketplaceSalePayment: MarketplaceSalePayment;
  deleteMarketplacePaymentActionsRef?: MutableRefObject<DeleteMarketplacePaymentRefActions | null>;
}

export const DeleteMarketplacePayment = ({
  deleteMarketplacePaymentActionsRef,
  marketplaceSalePayment,
}: DeleteMarketplacePaymentProps) => {
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { activeAccountWebClientConfig } = useAppContext();
  const deleteMarketplacePaymentDialog = useBasicDialog();
  const { showErrorDialog } = useClientLogContext();

  const onCancel = useCallback(() => {
    deleteMarketplacePaymentDialog.closeDialog();
  }, [deleteMarketplacePaymentDialog]);

  const onDelete = useCallback(
    async (keepSaleLineItemLinkedWithSale: boolean) => {
      setIsDeleting(true);
      await tryInvokeApi(
        async () => {
          const success = await new MarketplacePaymentClient(
            activeAccountWebClientConfig
          ).deleteMarketplacePayment(
            marketplaceSalePayment.id,
            keepSaleLineItemLinkedWithSale
          );

          if (success) {
            const isExpanded = true;
            queryClient.invalidateQueries({
              queryKey: [PAYMENT_SINGLE_MONTH_QUERY_KEY, isExpanded],
              refetchType: 'active',
            });
            deleteMarketplacePaymentDialog.closeDialog();
          }
        },
        (error) => {
          showErrorDialog(
            'MarketplacePaymentClient.deleteMarketplacePayment',
            error as Error
          );
        },
        () => {
          setIsDeleting(false);
        }
      );
    },
    [
      activeAccountWebClientConfig,
      deleteMarketplacePaymentDialog,
      marketplaceSalePayment.id,
      queryClient,
      showErrorDialog,
    ]
  );

  useImperativeHandle(
    deleteMarketplacePaymentActionsRef,
    () => ({
      showDeleteMarketplacePaymentDialog: () =>
        deleteMarketplacePaymentDialog.launchDialog(),
    }),
    [deleteMarketplacePaymentDialog]
  );

  return (
    <>
      {deleteMarketplacePaymentDialog.dialogProps.isOpen && (
        <DeleteMarketplacePaymentDialog
          {...deleteMarketplacePaymentDialog.dialogProps}
          onDelete={onDelete}
          onCancel={onCancel}
          isLoading={isDeleting}
          paymentId={marketplaceSalePayment.id}
        />
      )}
    </>
  );
};
